<template>
  <div class="lang-page">
    <header-nav></header-nav>

    <div class="brand-line"></div>

    <div class="lang-shell">
      <!-- ===== 左侧导航 ===== -->
      <aside class="sidenav">
        <div class="brand">
          <div class="logo-mark">粉</div>
          <div class="btxt"><b>粉裙单词台</b><span>CET 词汇学习工作台</span></div>
        </div>

        <!-- 等级切换 -->
        <div class="level-switch">
          <div
            v-for="t in levelTabs"
            :key="t.key"
            class="lv-item"
            :class="{ on: currentLevel === t.key }"
            @click="switchLevel(t.key)">
            <span>{{ t.icon }}</span>{{ t.name }}
          </div>
        </div>

        <nav class="tabs">
          <button v-for="n in navItems" :key="n.key"
            class="tab" :class="{ on: page === n.key }"
            @click="switchPage(n.key)">
            <svg class="ti" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" v-html="n.icon"></svg>
            <span class="tl">{{ n.label }}</span>
            <span v-if="n.badge" class="cnt">{{ n.badge }}</span>
          </button>
        </nav>

        <div class="utils">
          <button class="hbtn add" @click="openAdd">＋ 新增词条</button>
          <button class="hbtn" @click="switchPage('set')">⚙ 设置</button>
        </div>
      </aside>

      <!-- ===== 主内容 ===== -->
      <div class="mainwrap">
        <header class="topbar">
          <div class="tb-l">
            <h1>{{ currentPageTitle }}</h1>
            <p>{{ currentPageSub }}</p>
          </div>
        </header>

        <div class="container">
          <main>
            <!-- ===== 学单词 ===== -->
            <section v-if="page === 'learn'" class="view on">
              <div class="searchrow">
                <input v-model="learnSearch" type="search" placeholder="搜索单词或释义…" autocomplete="off">
              </div>
              <div class="chips">
                <button v-for="c in catList" :key="c" class="chip" :class="{ on: curCat === c }" @click="curCat = c; learnBrowse = false; browseIdx = 0">
                  {{ c }}
                </button>
              </div>

              <template v-if="learnSearch">
                <div class="liblist">
                  <div v-for="w in searchResults" :key="w.word" class="librow" @click="showWordDetail = w">
                    <div class="li">
                      <div class="lw"><b>{{ w.word }}</b><span v-if="w.pos" class="tag">{{ w.pos }}</span><span class="ipa">{{ w.phonetic }}</span></div>
                      <div class="lm">{{ w.meaning }}</div>
                    </div>
                  </div>
                  <div v-if="!searchResults.length" class="empty">没有匹配的单词</div>
                </div>
              </template>

              <template v-else>
                <div v-if="currentLearn" class="card wcard">
                  <div class="word">
                    <span class="wpad"></span>
                    <span class="wmain">{{ currentLearn.word }}</span>
                    <span class="wmeta">
                      <span v-if="freqBadge(currentLearn)" class="freq-badge" :class="freqBadgeClass(currentLearn)">{{ freqBadge(currentLearn) }}</span>
                      <button class="speak" @click.stop="speak(currentLearn.word)"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c2325c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z" fill="#fdeef3"/><path d="M15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13"/></svg></button>
                    </span>
                  </div>
                  <div v-if="currentLearn.phonetic" class="ipa">{{ currentLearn.phonetic }}</div>
                  <div class="mean">
                    <span v-if="currentLearn.pos" style="color:var(--sub);margin-right:6px">{{ currentLearn.pos }}</span>
                    {{ currentLearn.meaning }}
                  </div>
                  <div v-if="currentLearn.example" class="sent">
                    <div class="stext">
                      {{ currentLearn.example }}
                      <span v-if="currentLearn.exampleZh" class="zh">{{ currentLearn.exampleZh }}</span>
                    </div>
                  </div>
                </div>
                <div v-else class="card" style="text-align:center;padding:40px 20px">
                  <div style="font-size:36px;margin-bottom:10px">🎉</div>
                  <div style="font-size:15px;font-weight:600;color:#141719;margin-bottom:6px">当前分类新词已学完！</div>
                  <div style="font-size:13px;color:#767d86;margin-bottom:18px">可以从头浏览所有词，或去复习页巩固</div>
                  <button class="morebtn" @click="startBrowse">📖 从头浏览所有词</button>
                </div>

                <div class="prog"><span>{{ learnBrowse ? '浏览模式：第 ' + (browseIdx + 1) + ' / ' + catFiltered.length + ' 个' : '已学 ' + learnedCount + ' / ' + catFiltered.length }}</span><span>进度 {{ learnPercent }}%</span></div>
                <div class="progbar"><i :style="{ width: learnPercent + '%' }"></i></div>

                <div class="acts" v-if="currentLearn && !learnBrowse">
                  <button class="btn-unknow" @click="markUnknown">😅 还不认识</button>
                  <button class="btn-skip" @click="markSkip">跳过</button>
                  <button class="btn-know" @click="markKnown">😊 认识了</button>
                </div>
                <div class="acts" v-if="learnBrowse">
                  <button class="btn-skip" @click="browsePrev">← 上一词</button>
                  <button class="btn-know" @click="stopBrowse">退出浏览</button>
                  <button class="btn-know" @click="browseNext">下一词 →</button>
                </div>
              </template>
            </section>

            <!-- ===== 复习打卡 ===== -->
            <section v-else-if="page === 'review'" class="view">
              <div class="statgrid">
                <div class="stat"><span>今日新学</span><b>{{ todayStat.n }}</b></div>
                <div class="stat"><span>今日复习</span><b>{{ todayStat.r }}</b></div>
                <div class="stat hot"><span>待复习</span><b>{{ dueList.length }}</b></div>
                <div class="stat"><span>连续打卡</span><b>{{ streakCount }}天</b></div>
              </div>

              <div class="card" style="padding:16px 20px;margin-bottom:16px">
                <div class="cardhd">复习队列</div>
                <div v-if="reviewQueue.length" class="reviewcard">
                  <div class="word">
                    <span class="wpad"></span>
                    <span class="wmain">{{ currentReview.word }}</span>
                    <span class="wmeta">
                      <button class="speak" @click.stop="speak(currentReview.word)"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c2325c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z" fill="#fdeef3"/><path d="M15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13"/></svg></button>
                    </span>
                  </div>
                  <div v-if="currentReview.phonetic" class="ipa">{{ currentReview.phonetic }}</div>
                  <div v-if="reviewRevealed" class="rmean">
                    <span v-if="currentReview.pos" style="color:var(--sub);margin-right:6px">{{ currentReview.pos }}</span>
                    {{ currentReview.meaning }}
                  </div>
                  <div v-if="reviewRevealed && currentReview.example" class="sent">
                    <div class="stext">
                      {{ currentReview.example }}
                      <span v-if="currentReview.exampleZh" class="zh">{{ currentReview.exampleZh }}</span>
                    </div>
                  </div>
                  <div v-if="!reviewRevealed" style="margin-top:14px;color:var(--faint);font-size:13px">💡 点击卡片或下方按钮看释义</div>
                </div>
                <div v-else class="empty">🎉 今日复习任务全部完成！</div>

                <div class="grade" v-if="currentReview">
                  <button class="g-no" @click="gradeReview('no')">❌ 忘记</button>
                  <button class="g-mid" @click="gradeReview('mid')">🤔 模糊</button>
                  <button class="g-ok" @click="gradeReview('ok')">✅ 认识</button>
                </div>
              </div>
            </section>

            <!-- ===== 生词本 ===== -->
            <section v-else-if="page === 'book'" class="view">
              <div class="card" style="padding:8px 20px">
                <div v-for="w in bookList" :key="w.word" class="librow">
                  <div class="li">
                    <div class="lw">
                      <b>{{ w.word }}</b>
                      <span v-if="w.pos" class="tag">{{ w.pos }}</span>
                      <span class="ipa">{{ w.phonetic }}</span>
                      <button class="sm" @click="speak(w.word)"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c2325c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z" fill="#fdeef3"/><path d="M15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13"/></svg></button>
                    </div>
                    <div class="lm">{{ w.meaning }}</div>
                  </div>
                  <div class="lops"><button @click="removeFromBook(w.word)">移除</button></div>
                </div>
                <div v-if="!bookList.length" class="empty">生词本是空的，遇到不会的词点「还不认识」就会自动加入</div>
              </div>
            </section>

            <!-- ===== 总词库 ===== -->
            <section v-else-if="page === 'lib'" class="view">
              <div class="searchrow">
                <input v-model="libSearch" type="search" :placeholder="`搜索全部 ${allWords.length} 词（单词 / 释义）…`" autocomplete="off">
              </div>
              <div class="libbar">
                <select v-model="libCat" class="lib-sel">
                  <option value="">全部分类</option>
                  <option v-for="c in catList" :key="c" :value="c">{{ c }}</option>
                </select>
                <select v-model="libFreq" class="lib-sel">
                  <option value="all">全部频次</option>
                  <option value="3">超高频</option>
                  <option value="2">高频</option>
                  <option value="1">核心</option>
                </select>
                <select v-model="libState" class="lib-sel">
                  <option value="all">全部状态</option>
                  <option value="new">未学</option>
                  <option value="learned">已学</option>
                  <option value="book">生词本内</option>
                </select>
                <select v-model="libSort" class="lib-sel">
                  <option value="freq">按词频高低</option>
                  <option value="az">按字母 A→Z</option>
                </select>
              </div>
              <div class="libmeta">
                <span>共 <b>{{ filteredLib.length }}</b> 词 · 已显示 {{ libShown }} 条</span>
                <button v-if="libShown < filteredLib.length" class="morebtn" style="padding:8px 18px;font-size:12.5px;min-height:36px" @click="libPage++">加载更多（还有 {{ filteredLib.length - libShown }} 条）</button>
              </div>
              <div class="liblist">
                <div v-for="w in filteredLib.slice(0, libShown)" :key="w.word" class="librow" :class="{ done: isLearned(w.word) }">
                  <div class="li">
                    <div class="lw">
                      <b>{{ w.word }}</b>
                      <span v-if="w.pos" class="tag">{{ w.pos }}</span>
                      <span v-if="w.phonetic" class="ipa">{{ w.phonetic }}</span>
                      <span v-if="w.category" class="tag">{{ w.category }}</span>
                      <button class="sm" @click="speak(w.word)"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c2325c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z" fill="#fdeef3"/><path d="M15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13"/></svg></button>
                    </div>
                    <div class="lm">{{ w.meaning }}</div>
                    <div v-if="w.example" class="lsent">{{ w.example }} <span v-if="w.exampleZh">—— {{ w.exampleZh }}</span></div>
                  </div>
                  <div class="lops">
                    <button @click="toggleBook(w.word)">{{ inBook(w.word) ? '移出生词本' : '+生词本' }}</button>
                    <button @click="quickMarkLearned(w.word)">{{ isLearned(w.word) ? '标记未学' : '标记已学' }}</button>
                  </div>
                </div>
                <div v-if="!filteredLib.length" class="empty">没有匹配的单词</div>
              </div>
            </section>

            <!-- ===== 拓展专区 ===== -->
            <section v-else-if="page === 'extras'" class="view">
              <div class="subtabs">
                <button class="chip" :class="{ on: extrasTab === 'conf' }" @click="extrasTab = 'conf'">形近词辨析</button>
                <button class="chip" :class="{ on: extrasTab === 'rare' }" @click="extrasTab = 'rare'">熟词偏义</button>
                <button class="chip" :class="{ on: extrasTab === 'ph' }" @click="extrasTab = 'ph'">高频词组</button>
                <button class="chip" :class="{ on: extrasTab === 'trans' }" @click="extrasTab = 'trans'">翻译词组</button>
              </div>

              <!-- 形近词辨析 -->
              <div v-if="extrasTab === 'conf'" class="two-col">
                <div v-for="(c, i) in confusableList" :key="i" class="card egroup">
                  <h4>
                    <span class="pair">{{ c.pair }}</span>
                    <button class="sm" @click="speak(c.words[0])"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c2325c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z" fill="#fdeef3"/><path d="M15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13"/></svg></button>
                    <button class="sm" v-if="c.words[1]" @click="speak(c.words[1])"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c2325c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z" fill="#fdeef3"/><path d="M15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13"/></svg></button>
                  </h4>
                  <div v-if="c.desc" class="desc">{{ c.desc }}</div>
                  <div v-if="c.egs && c.egs.length" class="egs">
                    <div v-for="(e, j) in c.egs" :key="j">
                      例：{{ typeof e === 'string' ? e : e.en }}
                      <span v-if="typeof e !== 'string' && e.zh" class="ezh">—— {{ e.zh }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="!confusableList.length" class="empty">当前等级暂无形近词数据</div>
              </div>

              <!-- 熟词偏义 -->
              <div v-else-if="extrasTab === 'rare'" class="two-col">
                <div v-for="(r, i) in rareList" :key="i" class="card bcard">
                  <div class="bw">{{ r.w }}<button class="sm" @click="speak(r.w)"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c2325c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z" fill="#fdeef3"/><path d="M15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13"/></svg></button></div>
                  <div class="row"><span class="lab">熟悉义</span><span>{{ r.c }}</span></div>
                  <div class="row"><span class="lab">偏义</span><span class="rare">{{ r.r }}</span></div>
                  <div v-if="r.e" class="beng">{{ r.e }}<i v-if="r.ez">—— {{ r.ez }}</i></div>
                </div>
                <div v-if="!rareList.length" class="empty">当前等级暂无熟词偏义数据</div>
              </div>

              <!-- 高频词组 -->
              <div v-else-if="extrasTab === 'ph'">
                <div class="searchrow">
                  <input v-model="phraseSearch" type="search" placeholder="搜索词组…" autocomplete="off">
                </div>
                <div class="libmeta">
                  <span>共 <b>{{ filteredPhrases.length }}</b> 条词组，已显示 {{ phShown }} 条</span>
                  <button v-if="phShown < filteredPhrases.length" class="morebtn" style="padding:8px 18px;font-size:12.5px;min-height:36px" @click="phPage++">加载更多（还有 {{ filteredPhrases.length - phShown }} 条）</button>
                </div>
                <div class="card ph-col">
                  <div v-for="(p, i) in filteredPhrases.slice(0, phShown)" :key="i" class="wrow">
                    <button class="sm" @click="speak(p.phrase)"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c2325c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z" fill="#fdeef3"/><path d="M15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13"/></svg></button>
                    <div class="wi">
                      <div class="ww">{{ p.phrase }}</div>
                      <div class="wm">{{ p.meaning }}</div>
                    </div>
                  </div>
                  <div v-if="!filteredPhrases.length" class="empty">没有匹配的词组</div>
                </div>
              </div>

              <!-- 翻译词组 -->
              <div v-else-if="extrasTab === 'trans'">
                <div class="searchrow">
                  <input v-model="transSearch" type="search" placeholder="搜索翻译词组（中英文均可）…" autocomplete="off">
                </div>
                <div class="chips">
                  <button class="chip" :class="{ on: transCat === '' }" @click="transCat = ''; trPage = 1">全部 {{ transList.length }}</button>
                  <button v-for="c in transCatList" :key="c" class="chip" :class="{ on: transCat === c }" @click="transCat = c; trPage = 1">{{ c }} {{ transCount(c) }}</button>
                </div>
                <div class="libmeta">
                  <span>共 <b>{{ filteredTrans.length }}</b> 个，已显示 {{ trShown }} 个</span>
                  <button v-if="trShown < filteredTrans.length" class="morebtn" style="padding:8px 18px;font-size:12.5px;min-height:36px" @click="trPage++">加载更多</button>
                </div>
                <div class="card ph-col">
                  <div v-for="(t, i) in filteredTrans.slice(0, trShown)" :key="i" class="wrow">
                    <div class="wi">
                      <div class="ww">{{ t.p }} <span v-if="t.cat" class="core-tag">{{ t.cat }}</span></div>
                      <div class="wm">{{ t.m }}</div>
                    </div>
                  </div>
                  <div v-if="!filteredTrans.length" class="empty">没有匹配的翻译词组</div>
                </div>
              </div>
            </section>

            <!-- ===== 设置 ===== -->
            <section v-else-if="page === 'set'" class="view">
              <div class="card" style="padding:20px;margin-bottom:14px">
                <div class="cardhd">每日新词上限</div>
                <div class="setrow">
                  <div class="sn"><b>每天新学词数</b><span>控制进度，避免贪多</span></div>
                  <div class="sc">
                    <select v-model.number="dailyNew" @change="saveDB" class="lib-sel">
                      <option :value="10">10</option>
                      <option :value="15">15</option>
                      <option :value="20">20（推荐）</option>
                      <option :value="30">30</option>
                      <option :value="999">不限</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="card" style="padding:20px;margin-bottom:14px">
                <div class="cardhd">数据备份</div>
                <div style="display:flex;gap:10px;flex-wrap:wrap">
                  <button class="morebtn" @click="exportDB">导出 JSON</button>
                  <button class="morebtn ghost" @click="$refs.fileInput.click()">导入恢复</button>
                  <button class="dangerbtn" @click="resetDB" style="width:auto;margin-top:0">清空记录</button>
                </div>
                <input ref="fileInput" type="file" accept=".json" style="display:none" @change="importDB">
              </div>
              <div class="card" style="padding:20px">
                <div class="cardhd">关于</div>
                <p style="font-size:13px;color:var(--sub);margin:0;line-height:1.7">
                  SRS 间隔：1/2/4/7/15/30/60 天。学单词页点「认识了」进入复习队列；复习页三档评分（认识/模糊/忘记）调整下次复习时间。学习记录按等级独立保存在浏览器本地。
                </p>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>

    <!-- ===== 新增词条弹窗 ===== -->
    <div v-if="addVisible" class="mask" @click="addVisible = false"></div>
    <div v-if="addVisible" class="modal">
      <div class="mhead"><b>新增词条</b><button class="mclose" @click="addVisible = false">×</button></div>
      <div class="mbody">
        <div class="seg">
          <button :class="{ on: addType === 'word' }" @click="addType = 'word'">单词</button>
          <button :class="{ on: addType === 'phrase' }" @click="addType = 'phrase'">词组 / 短语</button>
        </div>
        <label class="fld"><span>英文<i>*</i></span><input v-model="addEn" placeholder="例如：serendipity"></label>
        <label class="fld"><span>中文释义<i>*</i></span><input v-model="addZh" placeholder="例如：n. 意外发现美好事物的运气"></label>
        <label class="fld" v-if="addType === 'word'"><span>主题分类</span>
          <select v-model="addCat">
            <option value="">（默认）</option>
            <option v-for="c in catList" :key="c" v-if="c !== '全部'" :value="c">{{ c }}</option>
          </select>
        </label>
        <template v-if="addType === 'word'">
          <label class="fld"><span>例句（可选）</span><input v-model="addSent" placeholder="一句包含该词的英文例句"></label>
          <label class="fld"><span>例句翻译（可选）</span><input v-model="addSentZh" placeholder="例句的中文翻译"></label>
        </template>
        <div class="mhint">自建单词会加入「学单词 / 总词库」，自建词组会加入「拓展专区 · 高频词组」。</div>
        <button class="modal-submit" @click="submitAdd">确认添加</button>
      </div>
    </div>
  </div>
</template>

<script>
import HeaderNav from '../HeaderNav.vue'
import { BEGINNER_WORDS, BEGINNER_PHRASES } from './Linguistic-data/beginner-words'
import { CET4_WORDS, CET4_PHRASES } from './Linguistic-data/cet4-words'
import { CET6_WORDS, CET6_PHRASES } from './Linguistic-data/cet6-words'
import { CET4_CONFUSABLE, CET4_RARE, CET4_TRANS } from './Linguistic-data/cet4-extras'
import { CET6_CONFUSE } from './Linguistic-data/cet6-extras'

const DAY_MS = 86400000
const SRS_INT = [1, 2, 4, 7, 15, 30, 60]

export default {
  name: 'Language',
  components: { HeaderNav },
  data() {
    return {
      currentLevel: 'beginner',
      page: 'learn',
      curCat: '全部',
      learnSearch: '',
      learnIdx: 0,
      reviewIdx: 0,
      reviewRevealed: false,
      libSearch: '',
      libCat: '',
      libFreq: 'all',
      libState: 'all',
      libSort: 'freq',
      phraseSearch: '',
      dailyNew: 20,
      extrasTab: 'ph',
      addVisible: false,
      addType: 'word',
      addEn: '',
      addZh: '',
      addCat: '',
      addSent: '',
      addSentZh: '',
      userWords: [],
      userPhrases: [],
      transCat: '',
      transSearch: '',
      phPage: 1,
      trPage: 1,
      libPage: 1,
      learnBrowse: false,
      browseIdx: 0,
      // 状态：words[w] = { s, d, w }  s: -1跳过/0新学/>0阶段; d:到期日epoch; w:错次
      // book: [单词数组]; daily: { dateKey: {n,r,ok} }
      state: { words: {}, book: [], daily: {}, pos: 0 }
    }
  },
  computed: {
    levelTabs() {
      return [
        { key: 'beginner', icon: '🌸', name: '初级' },
        { key: 'cet4', icon: '📘', name: '四级' },
        { key: 'cet6', icon: '🎓', name: '六级' }
      ]
    },
    navItems() {
      return [
        { key: 'learn', label: '学单词', icon: '<path d="M4 19V5a2 2 0 012-2h13v16H6a2 2 0 00-2 2zm0 0a2 2 0 002 2h13"/><path d="M9 7.5h6M9 11h6"/>' },
        { key: 'review', label: '复习打卡', icon: '<path d="M9 11.5l2.5 2.5L20 5.5"/><path d="M20 12.5V18a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h8"/>', badge: this.dueList.length || '' },
        { key: 'book', label: '生词本', icon: '<path d="M6.5 3H18a1 1 0 011 1v16l-6.5-3.2L6 20V4a1 1 0 011-1z"/>' },
        { key: 'lib', label: '总词库', icon: '<path d="M4 6h16M4 12h16M4 18h16"/>' },
        { key: 'extras', label: '拓展专区', icon: '<rect x="3" y="3" width="7.5" height="7.5" rx="1.6"/><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.6"/><rect x="3" y="13.5" width="7.5" height="7.5" rx="1.6"/><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.6"/>' }
      ]
    },
    currentPageTitle() {
      const m = { learn: '学单词', review: '复习打卡', book: '生词本', lib: '总词库', extras: '拓展专区', set: '设置' }
      return m[this.page] || '学单词'
    },
    currentPageSub() {
      const m = {
        learn: '按词频逐词攻克，认识后自动进入遗忘曲线复习',
        review: '根据艾宾浩斯遗忘曲线，到期词自动出现在这里',
        book: '标记「还不认识」的词会自动收集到这里',
        lib: '浏览全部单词，可按分类/频次/状态筛选',
        extras: '高频词组固定搭配',
        set: '学习记录与数据管理'
      }
      return m[this.page] || ''
    },
    allWords() {
      let base
      if (this.currentLevel === 'beginner') base = BEGINNER_WORDS
      else if (this.currentLevel === 'cet4') base = CET4_WORDS
      else base = CET6_WORDS
      return base.concat(this.userWords)
    },
    allPhrases() {
      if (this.currentLevel === 'beginner') return BEGINNER_PHRASES
      if (this.currentLevel === 'cet4') return CET4_PHRASES
      return CET6_PHRASES
    },
    catList() {
      const set = new Set(['全部'])
      this.allWords.forEach(w => { if (w.category) set.add(w.category) })
      return Array.from(set)
    },
    catFiltered() {
      return this.curCat === '全部' ? this.allWords : this.allWords.filter(w => w.category === this.curCat)
    },
    todayEpoch() { return Math.floor(Date.now() / DAY_MS) },
    todayKey() {
      const d = new Date()
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
    },
    todayStat() {
      return this.state.daily[this.todayKey] || { n: 0, r: 0, ok: 0 }
    },
    dueList() {
      const te = this.todayEpoch
      const out = []
      this.allWords.forEach(w => {
        const r = this.state.words[w.word]
        if (r && (r.s > 0 || (r.s === 0 && r.d > 0)) && r.d <= te) {
          out.push(w)
        }
      })
      return out
    },
    currentReview() {
      if (!this.reviewQueue.length) return null
      return this.reviewQueue[Math.min(this.reviewIdx, this.reviewQueue.length - 1)]
    },
    reviewQueue() { return this.dueList },
    learnedCount() {
      let n = 0
      this.allWords.forEach(w => { if (this.isLearned(w.word)) n++ })
      return n
    },
    learnPercent() {
      const total = this.catFiltered.length
      return total ? Math.round(this.learnedCount / total * 100) : 0
    },
    currentLearn() {
      if (this.learnBrowse) {
        const list = this.catFiltered
        if (!list.length) return null
        return list[Math.min(this.browseIdx, list.length - 1)]
      }
      const idx = this.nextLearnIdx()
      if (idx < 0) return null
      return this.catFiltered[idx]
    },
    searchResults() {
      const q = this.learnSearch.trim().toLowerCase()
      if (!q) return []
      return this.allWords.filter(w =>
        w.word.toLowerCase().includes(q) || w.meaning.toLowerCase().includes(q))
    },
    bookList() {
      return this.allWords.filter(w => this.inBook(w.word))
    },
    filteredPhrases() {
      const q = this.phraseSearch.trim().toLowerCase()
      if (!q) return this.allPhrases.concat(this.userPhrases)
      return this.allPhrases.concat(this.userPhrases).filter(p =>
        p.phrase.toLowerCase().includes(q) || p.meaning.toLowerCase().includes(q))
    },
    confusableList() {
      if (this.currentLevel === 'cet4') return CET4_CONFUSABLE
      if (this.currentLevel === 'cet6') return CET6_CONFUSE.map(c => ({
        pair: c.a + '/' + c.b, words: [c.a, c.b],
        mean: ['', ''], desc: '', egs: (c.egs || []).map(en => ({ en, zh: '' }))
      }))
      return []
    },
    rareList() { return this.currentLevel === 'cet4' ? CET4_RARE : [] },
    transList() {
      if (this.currentLevel === 'cet4') return CET4_TRANS
      return []
    },
    filteredTrans() {
      let arr = this.transList
      if (this.transCat) arr = arr.filter(t => t.cat === this.transCat)
      const q = this.transSearch.trim().toLowerCase()
      if (q) arr = arr.filter(t => t.p.toLowerCase().includes(q) || t.m.toLowerCase().includes(q))
      return arr
    },
    transCatList() {
      const set = new Set()
      this.transList.forEach(t => { if (t.cat) set.add(t.cat) })
      return Array.from(set)
    },
    phShown() { return Math.min(this.filteredPhrases.length, this.phPage * 50) },
    trShown() { return Math.min(this.filteredTrans.length, this.trPage * 50) },
    libShown() { return Math.min(this.filteredLib.length, this.libPage * 50) },
    filteredLib() {
      let arr = this.allWords.slice()
      if (this.libCat) arr = arr.filter(w => w.category === this.libCat)
      if (this.libFreq !== 'all') arr = arr.filter(w => (w.freq || 0) >= parseInt(this.libFreq))
      if (this.libState === 'new') arr = arr.filter(w => !this.isLearned(w.word))
      else if (this.libState === 'learned') arr = arr.filter(w => this.isLearned(w.word))
      else if (this.libState === 'book') arr = arr.filter(w => this.inBook(w.word))
      const q = this.libSearch.trim().toLowerCase()
      if (q) arr = arr.filter(w => w.word.toLowerCase().includes(q) || w.meaning.toLowerCase().includes(q))
      if (this.libSort === 'az') arr.sort((a, b) => a.word < b.word ? -1 : a.word > b.word ? 1 : 0)
      else arr.sort((a, b) => (b.freq || 0) - (a.freq || 0))
      return arr
    },
    streakCount() {
      let n = 0
      const d = new Date()
      if (!this.activeDay(this.keyOf(d))) d.setDate(d.getDate() - 1)
      while (this.activeDay(this.keyOf(d))) { n++; d.setDate(d.getDate() - 1) }
      return n
    }
  },
  created() { this.loadDB() },
  watch: {
    page() { this.reviewRevealed = false; this.reviewIdx = 0 },
    libSearch() { this.libPage = 1 },
    libCat() { this.libPage = 1 },
    libFreq() { this.libPage = 1 },
    libState() { this.libPage = 1 },
    libSort() { this.libPage = 1 },
    phraseSearch() { this.phPage = 1 },
    transSearch() { this.trPage = 1 },
    currentLevel() { this.phPage = 1; this.trPage = 1; this.libPage = 1 }
  },
  methods: {
    keyOf(d) {
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
    },
    activeDay(k) {
      const s = this.state.daily[k]
      return !!(s && (s.n + s.r) > 0)
    },
    storageKey() { return 'pink_skirt_lang_' + this.currentLevel + '_v3' },
    loadDB() {
      try {
        const raw = localStorage.getItem(this.storageKey())
        if (raw) {
          const o = JSON.parse(raw)
          this.state = {
            words: o.words || {},
            book: o.book || [],
            daily: o.daily || {},
            pos: o.pos || 0
          }
          this.dailyNew = o.dailyNew || 20
        } else {
          this.state = { words: {}, book: [], daily: {}, pos: 0 }
        }
      } catch (e) {
        this.state = { words: {}, book: [], daily: {}, pos: 0 }
      }
      this.loadUserItems()
    },
    saveDB() {
      try {
        localStorage.setItem(this.storageKey(), JSON.stringify({
          words: this.state.words, book: this.state.book,
          daily: this.state.daily, pos: this.state.pos, dailyNew: this.dailyNew
        }))
      } catch (e) {}
    },
    wordRec(w) {
      if (!this.state.words[w]) this.$set(this.state.words, w, { s: 0, d: 0, w: 0 })
      return this.state.words[w]
    },
    isLearned(w) {
      const r = this.state.words[w]
      return !!r && r.d > 0
    },
    isSkipped(w) {
      const r = this.state.words[w]
      return !!r && r.s === -1
    },
    inBook(w) { return this.state.book.indexOf(w) >= 0 },
    dayStat() {
      const k = this.todayKey
      if (!this.state.daily[k]) this.$set(this.state.daily, k, { n: 0, r: 0, ok: 0 })
      return this.state.daily[k]
    },
    nextLearnIdx() {
      const list = this.catFiltered
      for (let i = this.state.pos; i < list.length; i++) {
        if (!this.isLearned(list[i].word) && !this.isSkipped(list[i].word)) return i
      }
      for (let i = 0; i < list.length; i++) {
        if (!this.isLearned(list[i].word) && !this.isSkipped(list[i].word)) return i
      }
      return -1
    },
    markKnown() {
      const w = this.currentLearn
      if (!w) return
      const r = this.wordRec(w.word)
      r.s = 0; r.d = this.todayEpoch + 1
      const st = this.dayStat()
      st.n += 1
      this.state.pos = this.nextLearnIdx() + 1
      this.saveDB()
    },
    markUnknown() {
      const w = this.currentLearn
      if (!w) return
      this.markKnown()
      if (!this.inBook(w.word)) this.state.book.push(w.word)
      this.saveDB()
    },
    markSkip() {
      const w = this.currentLearn
      if (!w) return
      const r = this.wordRec(w.word)
      r.s = -1
      this.state.pos = this.nextLearnIdx() + 1
      this.saveDB()
    },
    startBrowse() {
      this.learnBrowse = true
      this.browseIdx = 0
    },
    stopBrowse() {
      this.learnBrowse = false
      this.browseIdx = 0
    },
    browseNext() {
      if (this.browseIdx < this.catFiltered.length - 1) this.browseIdx++
      else this.browseIdx = 0
    },
    browsePrev() {
      if (this.browseIdx > 0) this.browseIdx--
      else this.browseIdx = this.catFiltered.length - 1
    },
    quickMarkLearned(word) {
      const r = this.wordRec(word)
      r.s = 0; r.d = this.todayEpoch + 1
      this.saveDB()
      this.$message.success('已标记 ' + word + ' 为已学')
    },
    toggleBook(word) {
      if (this.inBook(word)) {
        const i = this.state.book.indexOf(word)
        this.state.book.splice(i, 1)
      } else {
        this.state.book.push(word)
      }
      this.saveDB()
    },
    removeFromBook(word) {
      const i = this.state.book.indexOf(word)
      if (i >= 0) this.state.book.splice(i, 1)
      this.saveDB()
    },
    gradeReview(grade) {
      const w = this.currentReview
      if (!w) return
      const r = this.wordRec(w.word)
      const st = this.dayStat()
      st.r += 1
      if (grade === 'ok') {
        r.s = Math.min(r.s + 1, SRS_INT.length - 1)
        st.ok += 1
        r.d = this.todayEpoch + SRS_INT[r.s]
      } else if (grade === 'mid') {
        r.d = this.todayEpoch + 1
      } else {
        r.s = 0; r.w = (r.w || 0) + 1
        if (!this.inBook(w.word)) this.state.book.push(w.word)
        r.d = this.todayEpoch + 1
      }
      this.saveDB()
      this.reviewRevealed = false
      if (this.reviewIdx < this.reviewQueue.length - 1) this.reviewIdx++
      else this.reviewIdx = 0
    },
    freqBadge(w) {
      const f = w.freq || 0
      if (f >= 3) return '超高频'
      if (f === 2) return '高频'
      if (f === 1) return '核心'
      return ''
    },
    freqBadgeClass(w) {
      const f = w.freq || 0
      if (f >= 3) return 'f3'
      if (f === 2) return 'f2'
      return 'f1'
    },
    switchLevel(key) {
      if (this.currentLevel === key) return
      this.currentLevel = key
      this.page = 'learn'
      this.curCat = '全部'
      this.learnSearch = ''
      this.phraseSearch = ''
      this.reviewIdx = 0
      this.reviewRevealed = false
      this.learnBrowse = false
      this.browseIdx = 0
      this.loadDB()
    },
    switchPage(p) { this.page = p },
    transCount(c) {
      return this.transList.filter(t => t.cat === c).length
    },
    speak(text) {
      if (!text) return
      if (!('speechSynthesis' in window)) { this.$message.warning('浏览器不支持语音朗读'); return }
      window.speechSynthesis.cancel()
      const u = new SpeechSynthesisUtterance(text)
      u.lang = 'en-US'; u.rate = 0.85
      const voices = window.speechSynthesis.getVoices()
      const en = voices.find(v => v.lang && v.lang.toLowerCase().startsWith('en'))
      if (en) u.voice = en
      window.speechSynthesis.speak(u)
    },
    exportDB() {
      const data = JSON.stringify({ level: this.currentLevel, ...this.state, dailyNew: this.dailyNew }, null, 2)
      const blob = new Blob([data], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const d = new Date()
      a.href = url
      a.download = 'word-' + this.currentLevel + '-' + this.keyOf(d) + '.json'
      a.click()
      URL.revokeObjectURL(url)
    },
    importDB(e) {
      const file = e.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = ev => {
        try {
          const o = JSON.parse(ev.target.result)
          this.state = { words: o.words || {}, book: o.book || [], daily: o.daily || {}, pos: o.pos || 0 }
          this.dailyNew = o.dailyNew || 20
          this.saveDB()
          this.$message.success('导入成功')
        } catch (err) { this.$message.error('导入失败') }
      }
      reader.readAsText(file)
      e.target.value = ''
    },
    resetDB() {
      this.$confirm('确定清空当前等级所有学习记录？此操作不可恢复。', '提示', { type: 'warning' })
        .then(() => {
          this.state = { words: {}, book: [], daily: {}, pos: 0 }
          this.saveDB()
          this.$message.success('已清空')
        }).catch(() => {})
    },
    openAdd() {
      this.addVisible = true
      this.addType = 'word'
      this.addEn = ''; this.addZh = ''; this.addCat = ''; this.addSent = ''; this.addSentZh = ''
    },
    submitAdd() {
      if (!this.addEn.trim() || !this.addZh.trim()) {
        this.$message.warning('请填写英文和中文释义')
        return
      }
      if (this.addType === 'word') {
        this.userWords.push({
          word: this.addEn.trim(),
          phonetic: '',
          pos: '',
          meaning: this.addZh.trim(),
          category: this.addCat || '自建',
          example: this.addSent,
          exampleZh: this.addSentZh,
          freq: 1
        })
      } else {
        this.userPhrases.push({
          phrase: this.addEn.trim(),
          meaning: this.addZh.trim(),
          example: '',
          exampleZh: ''
        })
      }
      this.saveUserItems()
      this.addVisible = false
      this.$message.success('添加成功')
    },
    saveUserItems() {
      try {
        const k = 'pink_skirt_lang_' + this.currentLevel + '_user'
        localStorage.setItem(k, JSON.stringify({ words: this.userWords, phrases: this.userPhrases }))
      } catch (e) {}
    },
    loadUserItems() {
      try {
        const k = 'pink_skirt_lang_' + this.currentLevel + '_user'
        const raw = localStorage.getItem(k)
        if (raw) {
          const o = JSON.parse(raw)
          this.userWords = o.words || []
          this.userPhrases = o.phrases || []
        } else {
          this.userWords = []; this.userPhrases = []
        }
      } catch (e) { this.userWords = []; this.userPhrases = [] }
    }
  }
}
</script>

<style scoped>
.lang-page {
  min-height: 100vh;
  padding-top: 60px;
  background: linear-gradient(180deg, #f8f3f6 0%, #f4f5f7 480px);
  color: #141719;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", Roboto, sans-serif;
}
.brand-line {
  position: fixed; top: 60px; left: 0; right: 0; height: 2px; z-index: 85;
  background: linear-gradient(90deg, #e2597f, #b9305c 46%, #c2325c);
}

/* ===== 左侧导航 ===== */
.lang-shell { display: block; }
.sidenav {
  position: fixed; left: 0; top: 62px; bottom: 0; width: 236px; z-index: 80;
  display: flex; flex-direction: column;
  background: #fff; border-right: 1px solid #e5e7eb;
  padding: 0; overflow-y: auto;
}
.brand { display: flex; align-items: center; gap: 10px; padding: 18px 16px 14px; border-bottom: 1px solid #f0f1f3; }
.logo-mark {
  width: 36px; height: 36px; border-radius: 9px; flex: none;
  background: linear-gradient(135deg, #c2325c, #e2597f);
  color: #fff; display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 15px;
}
.btxt b { display: block; font-size: 15px; font-weight: 700; color: #141719; line-height: 1.3; }
.btxt span { display: block; font-size: 10.5px; color: #767d86; margin-top: 1px; }

.level-switch { padding: 10px 12px; display: flex; flex-direction: column; gap: 2px; border-bottom: 1px solid #f0f1f3; }
.lv-item {
  display: flex; align-items: center; gap: 7px;
  padding: 8px 12px; border-radius: 8px;
  font-size: 13px; color: #464c53; cursor: pointer;
  transition: background .15s;
}
.lv-item:hover { background: #f2f3f5; }
.lv-item.on { background: #fdeef3; color: #c2325c; font-weight: 600; }

.tabs { flex: 1; padding: 10px 12px; display: flex; flex-direction: column; gap: 2px; }
.tab {
  position: relative; display: inline-flex; align-items: center; gap: 8px;
  padding: 9px 12px; border-radius: 8px; background: none; border: 0;
  color: #464c53; font-size: 13.5px; font-weight: 500;
  min-height: 38px; white-space: nowrap; cursor: pointer;
  transition: background .15s, color .15s;
}
.tab:hover { background: #f2f3f5; color: #141719; }
.tab.on { background: #fdeef3; color: #c2325c; font-weight: 600; }
.tab.on::before {
  content: ''; position: absolute; left: -12px; top: 50%; transform: translateY(-50%);
  width: 3px; height: 18px; border-radius: 0 3px 3px 0; background: #c2325c;
}
.tab .ti { flex: none; }
.tab .cnt {
  margin-left: auto; background: #c2325c; color: #fff;
  font-size: 10.5px; font-weight: 700; border-radius: 999px;
  padding: 0 6px; line-height: 16px; min-width: 16px; text-align: center;
}
.utils { padding: 14px; border-top: 1px solid #f0f1f3; }
.hbtn {
  width: 100%; background: #fff; border: 1px solid #e5e7eb; color: #464c53;
  border-radius: 8px; padding: 8px 12px; font-size: 12.5px; font-weight: 600;
  display: inline-flex; align-items: center; justify-content: center; gap: 5px;
  min-height: 34px; cursor: pointer;
}
.hbtn:hover { border-color: #f5d7e1; color: #c2325c; background: #fdeef3; }

/* ===== 主内容 ===== */
.mainwrap { margin-left: 236px; }
.topbar {
  position: sticky; top: 62px; z-index: 60;
  background: rgba(255,255,255,.9);
  -webkit-backdrop-filter: blur(14px); backdrop-filter: blur(14px);
  border-bottom: 1px solid #e5e7eb;
  padding: 15px 28px;
}
.tb-l { position: relative; padding-left: 13px; }
.tb-l::before {
  content: ''; position: absolute; left: 0; top: 5px; bottom: 5px; width: 3px;
  border-radius: 999px; background: linear-gradient(180deg, #e2597f, #b62f5a);
}
.tb-l h1 { font-size: 18px; font-weight: 700; letter-spacing: -.015em; color: #141719; line-height: 1.3; margin: 0; }
.tb-l p { font-size: 12.5px; color: #767d86; margin: 3px 0 0; }

.container { max-width: 1180px; margin: 0 auto; padding: 20px 28px 96px; }
.view { display: block; animation: fade .18s ease; }
@keyframes fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }

/* ===== 通用卡片 ===== */
.card {
  background: #fff; border: 1px solid #e5e7eb; border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16,20,24,.05), 0 8px 22px rgba(16,20,24,.05);
  padding: 20px;
}
.cardhd { font-size: 12.5px; font-weight: 600; color: #464c53; letter-spacing: .01em; margin-bottom: 12px; }

/* ===== 搜索/chips ===== */
.searchrow { display: flex; gap: 10px; margin-bottom: 14px; align-items: center; }
.searchrow input {
  flex: 1; border: 1px solid #e5e7eb; border-radius: 8px; padding: 0 14px;
  font-size: 16px; outline: none; background: #fff; color: #141719; height: 42px;
}
.searchrow input:focus { border-color: #c2325c; box-shadow: 0 0 0 3px rgba(194,50,92,.09); }
.chips { display: flex; gap: 7px; flex-wrap: wrap; margin-bottom: 14px; }
.chip {
  padding: 7px 13px; border-radius: 7px; background: #fff; border: 1px solid #e5e7eb;
  color: #464c53; font-size: 13px; font-weight: 500; min-height: 36px; cursor: pointer;
  transition: border-color .15s, color .15s, background .15s; white-space: nowrap;
}
.chip:hover { border-color: #f5d7e1; color: #c2325c; }
.chip.on {
  background: linear-gradient(135deg, #d5506f, #b02c56);
  border-color: transparent; color: #fff; font-weight: 600;
  box-shadow: 0 2px 8px rgba(194,50,92,.28);
}

/* ===== 学单词闪卡 ===== */
.wcard { text-align: center; padding: 28px 20px 20px; position: relative; overflow: hidden; background: linear-gradient(180deg, #fff 0%, #fdf6f8 140%); margin-bottom: 14px; }
.wcard::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, #e2597f, #c2325c 55%, #a92750);
}
.wcard .word, .reviewcard .word {
  display: flex; align-items: center; justify-content: center;
  font-size: 40px; font-weight: 700; letter-spacing: -.015em; line-height: 1.15;
  font-family: Georgia, "Times New Roman", serif; color: #141719;
}
.wcard .wpad, .reviewcard .wpad { flex: none; }
.wcard .wmeta, .reviewcard .wmeta { display: inline-flex; align-items: center; flex: none; margin-left: 14px; gap: 6px; }
.wcard .ipa, .reviewcard .ipa { color: #767d86; font-size: 14.5px; margin-top: 8px; font-family: Georgia, serif; }
.wcard .speak, .reviewcard .speak {
  width: 42px; height: 42px; border-radius: 8px;
  background: #f3f4f6; color: #c2325c; border: 0; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
}
.wcard .speak:hover, .reviewcard .speak:hover { background: #fdeef3; }
.freq-badge {
  display: inline-block; font-size: 11px; border-radius: 5px; padding: 2px 7px;
  font-weight: 600; border: 1px solid transparent;
}
.f3 { background: #fdeef3; color: #c2325c; border-color: #f5d7e1; }
.f2 { background: #fdf4e6; color: #a96a12; border-color: #f2e0bf; }
.f1 { background: #f3f4f6; color: #6b7280; border-color: #e5e7eb; }
.wcard .mean {
  margin-top: 12px; font-size: 16px; min-height: 46px;
  display: flex; align-items: center; justify-content: center;
  color: #464c53; line-height: 1.7; font-weight: 500;
}
.wcard .sent, .reviewcard .sent {
  margin-top: 16px; background: #f8f9fa; border: 1px solid #f0f1f3; border-radius: 9px;
  padding: 14px 16px; font-size: 14px; color: #464c53; max-width: 660px;
  margin-left: auto; margin-right: auto; text-align: left; line-height: 1.7;
}
.wcard .sent .zh, .reviewcard .sent .zh { display: block; margin-top: 6px; font-size: 13px; color: #8f6a7a; }

.prog { display: flex; justify-content: space-between; margin-top: 14px; font-size: 12.5px; color: #767d86; }
.progbar { height: 5px; background: #eceef1; border-radius: 999px; margin-top: 8px; overflow: hidden; }
.progbar i { display: block; height: 100%; border-radius: 999px; background: linear-gradient(90deg, #e2597f, #c2325c); transition: width .4s ease; }

.acts { display: flex; gap: 10px; margin-top: 16px; flex-wrap: wrap; }
.acts button {
  flex: 1; border-radius: 8px; height: 48px; padding: 0 12px;
  font-size: 15px; font-weight: 600; min-height: 48px; cursor: pointer;
}
.btn-know { background: #fff; border: 1px solid #e5e7eb; color: #464c53; }
.btn-know:hover { border-color: #f5d7e1; color: #c2325c; background: #fdeef3; }
.btn-unknow {
  background: linear-gradient(135deg, #d5506f, #b02c56);
  border: 1px solid transparent; color: #fff;
  box-shadow: 0 3px 10px rgba(194,50,92,.32);
}
.btn-skip { background: #fff; border: 1px solid #e5e7eb; color: #767d86; flex: 0 0 96px !important; }

/* ===== 复习页统计 ===== */
.statgrid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px; }
.stat {
  background: #fff; border: 1px solid #e5e7eb; border-radius: 12px;
  padding: 14px 20px 13px; box-shadow: 0 1px 2px rgba(16,20,24,.05), 0 8px 22px rgba(16,20,24,.05);
  display: flex; flex-direction: column; transition: box-shadow .18s, transform .18s;
}
.stat:hover { box-shadow: 0 10px 26px rgba(16,20,24,.09); transform: translateY(-2px); }
.stat span { order: 1; font-size: 11.5px; color: #767d86; font-weight: 500; }
.stat b { order: 2; font-size: 26px; font-weight: 700; color: #141719; line-height: 1.2; margin-top: 2px; letter-spacing: -.02em; }
.stat.hot b { color: #c2325c; }

.reviewcard { position: relative; overflow: hidden; background: linear-gradient(180deg, #fff 0%, #fdf6f8 140%); text-align: center; padding: 28px 20px 20px; margin-bottom: 14px; }
.reviewcard::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, #e2597f, #c2325c 55%, #a92750);
}
.reviewcard .rmean { text-align: center; margin-top: 12px; color: #464c53; font-size: 16px; font-weight: 500; }
.grade { display: flex; gap: 10px; max-width: 440px; margin: 18px auto 0; }
.grade button {
  flex: 1; border-radius: 8px; height: 46px; font-size: 14.5px; font-weight: 600;
  min-height: 46px; border: 1px solid transparent; cursor: pointer;
}
.g-ok { background: #e9f5ee; color: #0f7a4d; border-color: #cbe8d8; }
.g-mid { background: #fdf4e6; color: #a96a12; border-color: #f2e0bf; }
.g-no { background: #fdecea; color: #c0392b; border-color: #f6d2cc; }

/* ===== 词库列表 ===== */
.libbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 12px; }
.lib-sel {
  border: 1px solid #e5e7eb; border-radius: 8px; padding: 0 11px; font-size: 14px;
  background: #fff; color: #464c53; height: 42px; min-height: 42px; outline: none;
}
.lib-sel:focus { border-color: #c2325c; box-shadow: 0 0 0 3px rgba(194,50,92,.09); }
.libmeta { display: flex; justify-content: space-between; font-size: 12.5px; color: #767d86; padding: 9px 0; }
.liblist {
  background: #fff; border: 1px solid #e5e7eb; border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16,20,24,.05), 0 8px 22px rgba(16,20,24,.05);
  padding: 6px 20px 10px;
}
.librow { display: flex; align-items: flex-start; gap: 12px; padding: 13px 0; border-bottom: 1px solid #f0f1f3; }
.librow:last-child { border-bottom: none; }
.librow .li { flex: 1; min-width: 0; }
.librow .lw { display: flex; align-items: center; gap: 7px; flex-wrap: wrap; }
.librow .lw b { font-family: Georgia, "Times New Roman", serif; font-size: 16px; color: #141719; font-weight: 700; }
.librow .ipa { color: #767d86; font-size: 12px; }
.librow .sm {
  width: 34px; height: 34px; border-radius: 8px; background: #f3f4f6; color: #c2325c;
  display: inline-flex; align-items: center; justify-content: center; flex: none; border: 0; cursor: pointer;
}
.librow .sm:hover { background: #fdeef3; }
.librow .lm { font-size: 13px; color: #464c53; margin-top: 3px; }
.librow .lsent { font-size: 12.5px; color: #8f6a7a; margin-top: 3px; line-height: 1.65; }
.librow .lops { display: flex; flex-direction: column; gap: 6px; flex: none; }
.librow .lops button {
  border: 1px solid #e5e7eb; border-radius: 7px; padding: 6px 10px; font-size: 12px;
  color: #767d86; min-height: 32px; white-space: nowrap; background: #fff; cursor: pointer;
}
.librow .lops button:hover { border-color: #f5d7e1; color: #c2325c; background: #fdeef3; }
.librow .lops button.on { background: #fdeef3; border-color: #f5d7e1; color: #c2325c; font-weight: 600; }
.librow.done .lw b { color: #0f7a4d; }
.tag {
  display: inline-block; font-size: 10.5px; border-radius: 5px; padding: 1px 6px;
  background: #f3f4f6; color: #6b7280; font-weight: 600; border: 1px solid #e5e7eb;
}
.empty { text-align: center; color: #767d86; padding: 34px 10px; font-size: 13.5px; line-height: 1.9; }

/* ===== 设置 ===== */
.setrow { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 11px 0; border-bottom: 1px solid #f0f1f3; }
.setrow:last-child { border-bottom: none; }
.setrow .sn b { display: block; font-size: 13px; font-weight: 600; color: #141719; }
.setrow .sn span { display: block; font-size: 11.5px; color: #767d86; margin-top: 2px; }
.morebtn {
  background: linear-gradient(135deg, #d5506f, #b02c56); color: #fff; border: none;
  border-radius: 8px; padding: 0 20px; font-size: 13.5px; font-weight: 600;
  height: 42px; min-height: 42px; cursor: pointer;
  box-shadow: 0 1px 3px rgba(194,50,92,.22);
}
.morebtn.ghost { background: #fff; color: #c2325c; border: 1px solid #f5d7e1; box-shadow: none; }
.dangerbtn {
  width: 100%; border-radius: 8px; font-size: 13.5px; font-weight: 600;
  height: 42px; min-height: 42px; background: #fff; color: #c0392b;
  border: 1px solid #f6d2cc; margin-top: 9px; cursor: pointer;
}

/* ===== 拓展专区：形近词/熟词偏义 ===== */
.two-col { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.egroup, .bcard { padding: 16px 18px; }
.egroup h4 {
  margin: 0 0 10px; font-size: 16px; font-weight: 700;
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.egroup h4 .pair { font-family: Georgia, serif; color: #141719; }
.egroup .sm {
  width: 34px; height: 34px; border-radius: 8px; background: #f3f4f6; color: #c2325c;
  display: inline-flex; align-items: center; justify-content: center; flex: none; border: 0; cursor: pointer;
  transition: background .15s;
}
.egroup .sm:hover { background: #fdeef3; }
.egroup .desc {
  font-size: 12.5px; color: #767d86; line-height: 1.7; white-space: pre-line;
  background: #f8f9fa; border-radius: 8px; padding: 10px 12px; margin-bottom: 8px;
}
.egroup .egs { font-size: 12.5px; color: #464c53; line-height: 1.7; }
.egroup .egs > div { margin-top: 4px; }
.egroup .ezh { color: #8f6a7a; }
.bcard .bw {
  font-family: Georgia, serif; font-size: 19px; font-weight: 700; color: #141719;
  margin-bottom: 10px; display: flex; align-items: center; gap: 8px;
}
.bcard .bw .sm {
  width: 34px; height: 34px; border-radius: 8px; background: #f3f4f6; color: #c2325c;
  display: inline-flex; align-items: center; justify-content: center; flex: none; border: 0; cursor: pointer;
  transition: background .15s;
}
.bcard .bw .sm:hover { background: #fdeef3; }
.bcard .row { display: flex; gap: 8px; font-size: 13px; color: #464c53; line-height: 1.6; margin-bottom: 4px; }
.bcard .row .lab {
  flex: none; font-size: 11px; color: #767d86; background: #f3f4f6;
  border-radius: 5px; padding: 1px 7px; height: fit-content; margin-top: 2px;
}
.bcard .row .rare { color: #c2325c; font-weight: 600; }
.bcard .beng { font-size: 12.5px; color: #464c53; margin-top: 8px; line-height: 1.7; }
.bcard .beng i { display: block; color: #8f6a7a; font-style: normal; font-size: 12px; margin-top: 2px; }

/* ===== 拓展专区：词组/翻译列表 ===== */
.ph-col { padding: 6px 20px !important; display: grid; grid-template-columns: 1fr 1fr; gap: 0 24px; }
.wrow {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 0; border-bottom: 1px solid #f0f1f3;
}
.wrow:last-child { border-bottom: none; }
.wrow .sm {
  width: 36px; height: 36px; border-radius: 8px; background: #f3f4f6; color: #c2325c;
  display: inline-flex; align-items: center; justify-content: center; flex: none; border: 0; cursor: pointer;
  transition: background .15s;
}
.wrow .sm:hover { background: #fdeef3; }
.wrow .wi { flex: 1; min-width: 0; }
.wrow .ww { font-family: Georgia, "Times New Roman", serif; font-size: 16px; color: #141719; font-weight: 600; }
.wrow .wm { font-size: 12.5px; color: #767d86; margin-top: 1px; line-height: 1.5; }
.core-tag {
  display: inline-block; font-size: 10.5px; background: #fdeef3; color: #c2325c;
  border: 1px solid #f5d7e1; border-radius: 5px; padding: 1px 6px;
  font-weight: 600; margin-left: 6px; vertical-align: middle;
}

/* ===== 新增词条弹窗 ===== */
.mask {
  position: fixed; inset: 0; background: rgba(16,20,24,.45); z-index: 90;
}
.modal {
  position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
  width: 420px; max-width: calc(100vw - 32px); max-height: 88vh; overflow-y: auto;
  background: #fff; border-radius: 14px; z-index: 91;
  box-shadow: 0 20px 60px rgba(0,0,0,.2);
}
.mhead {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px; border-bottom: 1px solid #f0f1f3;
}
.mhead b { font-size: 15px; font-weight: 700; }
.mclose { border: none; background: none; font-size: 22px; color: #767d86; cursor: pointer; line-height: 1; }
.mbody { padding: 18px 20px; }
.seg {
  display: flex; background: #f3f4f6; border-radius: 8px; padding: 3px; margin-bottom: 14px;
}
.seg button {
  flex: 1; border: none; background: none; padding: 7px; border-radius: 6px;
  font-size: 13px; color: #767d86; cursor: pointer; font-weight: 500;
}
.seg button.on { background: #fff; color: #c2325c; font-weight: 600; box-shadow: 0 1px 3px rgba(0,0,0,.08); }
.fld { display: block; margin-bottom: 12px; }
.fld > span { display: block; font-size: 12px; color: #464c53; margin-bottom: 5px; font-weight: 500; }
.fld > span i { color: #c0392b; font-style: normal; margin-left: 2px; }
.fld input, .fld select {
  width: 100%; border: 1px solid #e5e7eb; border-radius: 8px;
  padding: 0 12px; height: 40px; font-size: 14px; outline: none; background: #fff;
}
.fld input:focus, .fld select:focus { border-color: #c2325c; box-shadow: 0 0 0 3px rgba(194,50,92,.09); }
.mhint { font-size: 11.5px; color: #a7aeb6; line-height: 1.6; margin: 8px 0 14px; }
.modal-submit {
  width: 100%; background: linear-gradient(135deg, #d5506f, #b02c56); color: #fff;
  border: none; border-radius: 8px; height: 44px; font-size: 14.5px; font-weight: 600;
  cursor: pointer; box-shadow: 0 3px 10px rgba(194,50,92,.28);
}
.hbtn.add { border-color: #f5d7e1; color: #c2325c; background: #fdeef3; font-weight: 700; }
.hbtn.add:hover { background: #c2325c; color: #fff; border-color: transparent; }

/* ===== 响应式 ===== */
@media (max-width: 860px) {
  .two-col { grid-template-columns: 1fr; }
  .ph-col { grid-template-columns: 1fr; }

  .sidenav { position: static; width: 100%; flex-direction: row; flex-wrap: wrap; padding: 10px; }
  .mainwrap { margin-left: 0; }
  .tabs { flex-direction: row; flex-wrap: wrap; }
  .tab.on::before { display: none; }
  .statgrid { grid-template-columns: repeat(2, 1fr); }
  .container { padding: 16px 14px 80px; }
  .wcard .word { font-size: 32px; }
}
</style>
